import supabase from '@/services/supabaseClient';

export const knowledgeBaseService = {
  // Get all categories with article counts
  async getCategories() {
    try {
      const { data: categories, error } = await supabase
        ?.from('knowledge_base_categories')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('sort_order');

      if (error) return { data: [], error };

      const { data: articleRows, error: articlesError } = await supabase
        ?.from('knowledge_base_articles')
        ?.select('category')
        ?.eq('status', 'published');

      if (articlesError) {
        return {
          data: (categories || []).map((category) => ({
            ...category,
            article_count: 0,
          })),
          error: null,
        };
      }

      const counts = (articleRows || []).reduce((acc, article) => {
        const key = String(article?.category || '').trim();
        if (!key) return acc;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});

      return {
        data: (categories || []).map((category) => ({
          ...category,
          article_count: counts[category?.category] || 0,
        })),
        error: null,
      };
    } catch (error) {
      return { data: [], error: { message: 'Failed to fetch categories' } };
    }
  },

  // Get published articles with optional filtering
  async getArticles(options = {}) {
    try {
      const { category, featured, limit = 50, search } = options;
      
      // Fixed query structure to avoid relationship issues
      let query = supabase
        ?.from('knowledge_base_articles')
        ?.select(`
          *,
          author_info:get_knowledge_base_author_info(author_id),
          category_info:knowledge_base_categories!category(
            display_name,
            icon_name
          )
        `)
        ?.eq('status', 'published')
        ?.order('created_at', { ascending: false });

      if (category) {
        query = query?.eq('category', category);
      }

      if (featured) {
        query = query?.eq('featured', true);
      }

      if (search) {
        query = query?.textSearch('search_vector', search);
      }

      if (limit) {
        query = query?.limit(limit);
      }

      const { data, error } = await query;

      return { data: data || [], error };
    } catch (error) {
      return { data: [], error: { message: 'Failed to fetch articles' } };
    }
  },

  // Alternative method using direct joins (fallback)
  async getArticlesDirectJoin(options = {}) {
    try {
      const { category, featured, limit = 50, search } = options;
      
      // Use simplified query to avoid complex relationship issues
      let query = supabase
        ?.from('knowledge_base_articles')
        ?.select(`
          *
        `)
        ?.eq('status', 'published')
        ?.order('created_at', { ascending: false });

      if (category) {
        query = query?.eq('category', category);
      }

      if (featured) {
        query = query?.eq('featured', true);
      }

      if (search) {
        query = query?.textSearch('search_vector', search);
      }

      if (limit) {
        query = query?.limit(limit);
      }

      const { data: articles, error } = await query;

      if (error) return { data: [], error };

      // Enhance articles with author information
      const enhancedArticles = await Promise.all(
        articles?.map(async (article) => {
          if (article?.author_id) {
            // Get author display name using the new function
            const { data: authorName } = await supabase
              ?.rpc('get_author_display_name', { author_user_id: article?.author_id });
            
            return {
              ...article,
              author: {
                display_name: authorName || 'Unknown Author'
              }
            };
          }
          return article;
        }) || []
      );

      return { data: enhancedArticles, error: null };
    } catch (error) {
      return { data: [], error: { message: 'Failed to fetch articles' } };
    }
  },

  // Get single article by slug
  async getArticle(slug) {
    try {
      // Try the enhanced query first
      const { data, error } = await supabase
        ?.from('knowledge_base_articles')
        ?.select(`
          *
        `)
        ?.eq('slug', slug)
        ?.eq('status', 'published')
        ?.single();

      if (error) return { data: null, error };

      // Enhance with author information
      let enhancedArticle = data;
      if (data?.author_id) {
        const { data: authorName } = await supabase
          ?.rpc('get_author_display_name', { author_user_id: data?.author_id });
        
        enhancedArticle = {
          ...data,
          author: {
            display_name: authorName || 'Unknown Author'
          }
        };
      }

      // Increment view count
      if (enhancedArticle) {
        await supabase
          ?.from('knowledge_base_articles')
          ?.update({ view_count: (enhancedArticle?.view_count || 0) + 1 })
          ?.eq('id', enhancedArticle?.id);
      }

      return { data: enhancedArticle || null, error: null };
    } catch (error) {
      return { data: null, error: { message: 'Failed to fetch article' } };
    }
  },

  // Get featured articles for home page
  async getFeaturedArticles(limit = 6) {
    // Use the fallback method for better reliability
    return this.getArticlesDirectJoin({ featured: true, limit });
  },

  // Search articles
  async searchArticles(searchTerm, limit = 20) {
    if (!searchTerm?.trim()) {
      return { data: [], error: null };
    }

    try {
      const { data, error } = await supabase
        ?.from('knowledge_base_articles')
        ?.select(`
          id,
          title,
          slug,
          excerpt,
          category,
          tags,
          created_at,
          view_count,
          category_info:knowledge_base_categories!category(
            display_name,
            icon_name
          )
        `)
        ?.eq('status', 'published')
        ?.textSearch('search_vector', searchTerm)
        ?.limit(limit);

      return { data: data || [], error };
    } catch (error) {
      return { data: [], error: { message: 'Search failed' } };
    }
  },

  // Vote on article helpfulness
  async voteArticle(articleId, isHelpful) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { error: { message: 'Authentication required' } };
      }

      // Upsert vote
      const { data, error } = await supabase
        ?.from('kb_article_votes')
        ?.upsert({
          article_id: articleId,
          user_id: user?.id,
          is_helpful: isHelpful
        })
        ?.select()
        ?.single();

      if (error) return { error };

      // Update article helpful votes count
      const { data: votes } = await supabase
        ?.from('kb_article_votes')
        ?.select('is_helpful')
        ?.eq('article_id', articleId)
        ?.eq('is_helpful', true);

      await supabase
        ?.from('knowledge_base_articles')
        ?.update({ helpful_votes: votes?.length || 0 })
        ?.eq('id', articleId);

      return { data, error: null };
    } catch (error) {
      return { error: { message: 'Failed to vote on article' } };
    }
  },

  // Get related articles
  async getRelatedArticles(articleId, category, limit = 4) {
    try {
      const { data, error } = await supabase
        ?.from('knowledge_base_articles')
        ?.select(`
          id,
          title,
          slug,
          excerpt,
          category,
          view_count,
          created_at
        `)
        ?.eq('status', 'published')
        ?.eq('category', category)
        ?.neq('id', articleId)
        ?.order('view_count', { ascending: false })
        ?.limit(limit);

      return { data: data || [], error };
    } catch (error) {
      return { data: [], error: { message: 'Failed to fetch related articles' } };
    }
  },

  // Get articles by category
  async getArticlesByCategory(category, limit = 20) {
    return this.getArticlesDirectJoin({ category, limit });
  }
};

export default knowledgeBaseService;
